import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import Monogram from "@/components/Monogram";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";
import { toast } from "sonner";

const HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_ivory-atelier/artifacts/r8ryeko6_download.mp4";

const MONOGRAM_SVG =
  "https://customer-assets.emergentagent.com/job_ivory-atelier/artifacts/vwz26ovp_monogram%20ivory.svg";

/* ───────────────────────────── HERO ───────────────────────────── */

function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
    >
      <video
        data-testid="hero-video"
        src={HERO_VIDEO}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Soft dark overlay just enough to keep ivory typography legible */}
      <div className="absolute inset-0 bg-[#2C2724]/25" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#2C2724]/15 via-transparent to-[#2C2724]/25" />

      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center px-6">
        <h1
          data-testid="hero-headline"
          className="font-display font-light text-[#F7F4EF] leading-[1.02] text-[8vw] sm:text-[6vw] lg:text-[5vw]"
          style={{
            letterSpacing: "0.015em",
            textShadow: "0 2px 24px rgba(44, 39, 36, 0.25)",
          }}
        >
          Beauty,
          <br />
          that <em className="italic">feels</em>
          <br />
          like you.
        </h1>
        <div className="mt-12 flex flex-col items-center gap-2">
          <p
            data-testid="hero-coming-soon"
            className="text-[12px] tracking-[0.5em] uppercase text-[#F7F4EF] font-medium"
            style={{ textShadow: "0 1px 12px rgba(44, 39, 36, 0.3)" }}
          >
            Coming Soon
          </p>
          <p
            className="text-[10px] tracking-[0.42em] uppercase text-[#F7F4EF] font-light"
            style={{ textShadow: "0 1px 12px rgba(44, 39, 36, 0.3)" }}
          >
            Kandivali West
          </p>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────── OUR APPROACH ───────────────────────────── */

function OurApproach() {
  return (
    <section
      data-testid="approach-section"
      className="bg-[#F5F2ED] py-24 lg:py-40 px-6 lg:px-14 relative overflow-hidden"
    >
      <Monogram
        size={620}
        opacity={0.04}
        color="#4A3E39"
        className="pointer-events-none absolute -right-40 top-1/2 -translate-y-1/2 hidden md:block"
      />
      <div className="max-w-[1200px] mx-auto text-center relative">
        <Reveal>
          <p className="eyebrow">01 — Our Approach</p>
        </Reveal>
        <Reveal delay={120}>
          <h2 className="mt-6 font-display font-light text-[#2C2724] leading-[1] tracking-[-0.01em] text-[42px] sm:text-[68px] lg:text-[88px]">
            Beauty that feels
            <br />
            <em className="italic">effortless.</em>
          </h2>
        </Reveal>
        <Reveal delay={260}>
          <div className="mt-8 flex flex-col items-center gap-2">
            <p className="font-display italic text-[#4A3E39] text-2xl md:text-3xl tracking-tight">
              Quiet confidence.
            </p>
            <p className="font-display italic text-[#4A3E39] text-2xl md:text-3xl tracking-tight">
              Intentional care.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────────────────── SERVICES ───────────────────────────── */

const SERVICE_MENU = [
  { title: "Hair", items: ["Cut", "Colour", "Styling", "Treatments"] },
  { title: "Nails", items: ["Manicure", "Gel", "Pedicure", "Art"] },
  { title: "Makeup", items: ["Occasion", "Editorial", "Bridal"] },
  { title: "Essentials", items: ["Waxing", "Threading", "Care"] },
];

function ServicesMenu() {
  return (
    <section
      data-testid="services-section"
      className="bg-[#EEE7DE] py-20 lg:py-32 px-6 lg:px-14 relative overflow-hidden"
    >
      <Monogram
        size={620}
        opacity={0.05}
        color="#4A3E39"
        className="pointer-events-none absolute -left-40 -bottom-40 hidden md:block"
      />
      <div className="max-w-[1200px] mx-auto relative">
        <Reveal>
          <p className="eyebrow text-center">02 — Services</p>
        </Reveal>

        <div className="mt-14 lg:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-12 lg:gap-y-0 gap-x-8 lg:gap-x-14">
          {SERVICE_MENU.map((col, idx) => (
            <Reveal key={col.title} delay={idx * 120}>
              <div className="text-center md:text-left lg:px-4">
                <h3
                  data-testid={`service-col-${col.title.toLowerCase()}`}
                  className="font-display font-light text-[#2C2724] text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight"
                >
                  {col.title}
                </h3>
                <div className="ivory-divider mt-5 opacity-60 md:w-12 mx-auto md:mx-0" />
                <ul className="mt-5 space-y-2.5">
                  {col.items.map((it) => (
                    <li
                      key={it}
                      className="font-display italic text-[#4A3E39] text-xl md:text-[22px] leading-snug tracking-tight"
                    >
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────── JOIN ───────────────────────────── */

const JOIN_CATEGORIES = ["Hair", "Nails", "Makeup", "Essentials"];

function JoinFirstAppointments() {
  const sectionRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service_category: "Hair",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/waitlist", form);
      if (data?.already_subscribed) {
        toast.success("You're already on the list. We'll be in touch soon.");
      } else {
        toast.success("Reserved. We'll write to you before we open.");
      }
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="join"
      ref={sectionRef}
      data-testid="join-section"
      className="bg-[#F5F2ED] py-24 lg:py-36 px-6 lg:px-14 relative overflow-hidden"
    >
      <Monogram
        size={720}
        opacity={0.05}
        color="#4A3E39"
        className="pointer-events-none absolute -right-60 -top-40 hidden md:block"
      />
      <Monogram
        size={520}
        opacity={0.04}
        color="#4A3E39"
        className="pointer-events-none absolute -left-40 -bottom-40 hidden md:block"
      />

      <div className="max-w-[1200px] mx-auto text-center relative">
        <Reveal>
          <p className="eyebrow">03 — Join</p>
        </Reveal>
        <Reveal delay={120}>
          <h2 className="mt-6 font-display font-light text-[#2C2724] leading-[1] tracking-[-0.01em] text-[40px] sm:text-[60px] lg:text-[76px]">
            Join the first
            <br />
            <em className="italic">appointments.</em>
          </h2>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-6 max-w-md mx-auto text-[14px] leading-[1.9] text-[#4A3E39]">
            We open in Kandivali West later this year. Leave your details to receive an
            invitation before public booking opens.
          </p>
        </Reveal>

        {submitted ? (
          <Reveal>
            <div data-testid="join-success" className="mt-14">
              <Monogram size={56} opacity={0.7} color="#6D1F2B" className="mx-auto" />
              <p className="mt-6 font-display italic text-[#2C2724] text-3xl md:text-4xl">
                Thank you, {form.name.split(" ")[0] || "friend"}.
              </p>
              <p className="mt-3 text-[13px] tracking-[0.28em] uppercase text-[#4A3E39]">
                We&rsquo;ll be in touch.
              </p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={360}>
            <form
              onSubmit={onSubmit}
              data-testid="join-form"
              className="mt-12 max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 text-left"
            >
              <div>
                <label className="eyebrow mb-3 block">Name</label>
                <input
                  data-testid="join-name"
                  type="text"
                  className="editorial-input"
                  value={form.name}
                  onChange={update("name")}
                  required
                />
              </div>
              <div>
                <label className="eyebrow mb-3 block">Phone Number</label>
                <input
                  data-testid="join-phone"
                  type="tel"
                  className="editorial-input"
                  placeholder="+91"
                  value={form.phone}
                  onChange={update("phone")}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow mb-4 block">Preferred Service</label>
                <div className="flex flex-wrap gap-3">
                  {JOIN_CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`join-category-${c.toLowerCase()}`}
                      onClick={() => setForm((f) => ({ ...f, service_category: c }))}
                      className={`text-[11px] tracking-[0.28em] uppercase px-5 py-3 border transition-colors ${
                        form.service_category === c
                          ? "bg-[#2C2724] text-[#F5F2ED] border-[#2C2724]"
                          : "border-[#B7AB9B] text-[#4A3E39] hover:border-[#2C2724]"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="join-submit"
                  className="btn-burgundy disabled:opacity-60"
                >
                  {submitting ? "Reserving…" : "Reserve a spot"}
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* ───────────────────────────── PAGE ───────────────────────────── */

export default function Home() {
  return (
    <Layout>
      <HeroSection />
      <OurApproach />
      <ServicesMenu />
      <JoinFirstAppointments />
    </Layout>
  );
}
